import { QueryClient } from '@tanstack/react-query'
import { HttpProblemError } from '@collector/shared'

/**
 * Every page in the app polls its server-state queries automatically so
 * that data uploaded by a tablet, approved by another admin, or deleted in
 * another tab shows up without a manual refresh.
 *
 *   - `refetchInterval: 15s` covers the "somebody else just did X" case
 *     while the tab is foregrounded.
 *   - `refetchIntervalInBackground: false` stops polling when the tab is
 *     hidden, so we don't burn cycles (and battery on laptops).
 *   - `refetchOnWindowFocus: true` snaps fresh data in the moment the user
 *     tabs back.
 *   - `refetchOnReconnect: true` covers flaky networks — the frontend
 *     revalidates as soon as the browser reports it's online again.
 *   - `staleTime: 5s` keeps navigation snappy: clicking back into a page
 *     you just visited doesn't trigger a fetch, but anything older
 *     qualifies for the next poll cycle.
 *
 * Individual queries can still opt out by passing `refetchInterval: false`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof HttpProblemError) {
          if (error.status === 401 || error.status === 403 || error.status === 404) return false
        }
        return failureCount < 1
      },
      staleTime: 1_000,
      refetchInterval: 1_000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
})
